import React from 'react';

export const curriculumData = {
  curriculum: [
    {
      title: 'Programming and GenAI Foundations',
      image: null,
      duration: '4 to 8 weeks',
      tag: null,
      data: (
        <>
          <p>
            Kickstart your developer journey by building a rock-solid foundation
            in programming, modern web tooling, and Generative AI — the three
            pillars every 2026 software engineer needs from day one.
          </p>
          <ul>
            <li>
              <span className="font-bold">Language Fundamentals: </span>
              Go from "Hello World" to confident coder. Master the syntax and
              constructs of Java or JavaScript (chosen based on your
              specialization), including loops, conditionals, arrays, objects,
              strings, data types, and standard libraries
            </li>
            <li>
              <span className="font-bold">Developer Essentials: </span>
              Pick up the must-have toolkit every working engineer uses every
              day, through hands-on labs and live workshops — HTTP, REST APIs,
              Linux, Git, SQL, and cloud deployment
            </li>
            <li>
              <span className="font-bold">GenAI Foundations: </span>
              Get fluent in the language of modern AI — how Large Language
              Models work, prompt engineering essentials, and the real-world use
              cases reshaping software development
            </li>
          </ul>
        </>
      ),
      skills: [
        'Loops',
        'Conditionals',
        'Arrays',
        'Objects',
        'Strings',
        'Data Types',
        'Libraries',
        'HTTP',
        'Rest API',
        'Deployment',
        'Linux',
        'GIT',
        'SQL',
        'LLMs',
        'Prompt Engineering',
        'Use Cases',
      ],
      anchor: 'programming-foundations',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Data Structures and Algorithms (with additional AI Guidance)',
      image: null,
      duration: '20 weeks',
      tag: null,
      data: (
        <>
          <p>
            Master DSA the way top product companies actually test it — through
            a carefully designed three-stage curriculum with exclusive videos,
            in-depth mentorship, live sessions, and continuous mock assessments.
          </p>

          <ul>
            <li>
              Basics — Build the Problem-Solving Muscle: Develop logic by
              solving math, recursion, and implementation problems. Get
              introduced to fundamental data structures (Linked Lists, Trees)
              and learn how to reason about Space & Time Complexity from day one
            </li>
          </ul>

          <ul>
            <li>
              Intermediate — Pattern Recognition for Real Interviews: Master the
              high-yield problem-solving patterns interviewers love — Two
              Pointers, Sliding Window, Prefix Sum, Binary Search, Bit
              Manipulation, BFS/DFS, Hash-based optimization. Learn the art of
              debugging under time pressure
            </li>
          </ul>

          <ul>
            <li>
              Advanced — The Interview-Cracking Edge: Step up to graph
              algorithms (Prim's, Kruskal's, Dijkstra's), greedy & backtracking
              patterns, Dynamic Programming (Memoization & Tabulation), and
              advanced data structures like Tries and Segment Trees
            </li>
          </ul>

          <ul>
            <li>
              AI Guidance: Get personalized feedback on your code, hints when
              you're stuck, and AI-powered explanations of optimal solutions
            </li>
          </ul>
        </>
      ),
      skills: [
        'Logic Building',
        'Collections',
        'Space and Time Complexity',
        'Matrix',
        'Two Pointers',
        'Sliding Window',
        'Sorting',
        'Prefix Sum',
        'Bit Manipulation',
        'Binary Search',
        'Data Structure Application',
        'Linked List',
        'Tree',
        'Graph',
        'BFS/DFS',
        'Dynamic Programming',
        'Greedy',
        'Knapsack',
        'Spanning Tree',
        'Trie',
        'Segment Tree',
      ],
      anchor: 'dsa',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Tech Stack Specialization & Work-like Projects',
      image: null,
      duration: '16 to 28 weeks',
      tag: null,
      data: (
        <>
          <p>
            This is the heart of the program — pick one of two specializations
            and ship a portfolio of work-like projects modelled on the apps used
            by AirBnB, Amazon, Netflix, Spotify, Flipkart and Swiggy.
          </p>

          <p>
            <span className="font-bold">Option 1 - </span>Fullstack
            Specialization (FDT): Become a full-stack engineer with a
            frontend-first lens. Ship 7 progressive projects:
          </p>
          <ul>
            <li>
              <span className="font-bold">QTrip Static</span> — AirBnB-like
              travel front end with HTML, CSS, Flexbox & Bootstrap
            </li>
            <li>
              <span className="font-bold">QTrip Dynamic</span> — Add API calls,
              routing, filters, data persistence and 3rd-party libraries
            </li>
            <li>
              <span className="font-bold">XBoard</span> (independent) — Google
              News-style newsfeed aggregator with JavaScript, DOM & deployment
            </li>
            <li>
              <span className="font-bold">QKart Frontend</span> —
              Amazon/Flipkart-style React shopping experience with hooks &
              Material UI
            </li>
            <li>
              <span className="font-bold">QTify</span> (independent) —
              Spotify-like audio streaming front end designed in Figma
            </li>
            <li>
              <span className="font-bold">QKart Backend</span> — Scalable
              Node.js / Express backend with MongoDB & REST APIs
            </li>
            <li>
              <span className="font-bold">XFlix</span> (independent) —
              Netflix-like backend with Express, Node, MongoDB & deployment
            </li>
          </ul>

          <p>
            Option 2 — Backend Specialization (BDT): Go deep on enterprise-grade
            Java backend systems. Ship 6 progressive projects:
          </p>
          <ul>
            <li>
              <span className="font-bold">QCalc</span> — Multi-file Java
              scientific calculator using OOP, Classes, Packages, Git & Gradle
            </li>
            <li>
              <span className="font-bold">XURL</span> (independent) —
              Bit.ly-like URL shortener applying all your Java & data structures
              knowledge
            </li>
            <li>
              <span className="font-bold">QMoney</span> — Zerodha-like visual
              stock portfolio analyser; consume REST APIs, refactor legacy code,
              write JUnit tests
            </li>
            <li>
              <span className="font-bold">XCompany</span> — Object Modeling &
              Machine Coding: model a company hierarchy with constraints
            </li>
            <li>
              <span className="font-bold">QEats</span> — Scalable distributed
              Java backend for a Swiggy-like food ordering app, with MVCS,
              Spring Boot, MongoDB, Mockito & Spring Security
            </li>
            <li>
              <span className="font-bold">XMeme (independent)</span> — Java
              backend for a Giphy-like meme platform applying everything you've
              learned
            </li>
          </ul>
        </>
      ),
      skills: [
        'HTML / CSS',
        'JavaScript',
        'React',
        'React Hooks',
        'Material UI',
        'Node.js',
        'Express',
        'MongoDB',
        'REST APIs',
        'Java',
        'OOP',
        'Spring Boot',
        'MVCS',
        'Jackson',
        'Gradle',
        'Refactoring',
        'Deployment',
      ],
      anchor: 'tech-stack',
      cardTitle: null,
      showOptionsAboveTitle: true,
      options: [
        { tag: 'fullstack-specialization', title: 'Fullstack Specialization' },
        { tag: 'backend-specialization', title: 'Backend Specialization' },
      ],
      cards: [],
    },
    {
      title: 'Master Software Development with AI',
      image: null,
      duration: '9 Sessions • 18 hours',
      tag: null,
      new: true,
      data: (
        <>
          <p>
            Go from zero AI background to confidently building real AI-powered
            software products — the foundation every modern developer needs in
            2026.
          </p>
          <ul>
            <li>How LLMs Work (And Why It Matters)</li>
            <li>
              Navigating the AI Model Landscape (OpenAI, Anthropic, open-source)
            </li>
            <li>Prompt Engineering That Actually Works</li>
            <li>
              Building with the OpenAI API (embeddings, function calling,
              streaming)
            </li>
            <li>Fine-Tuning — When & How to Train Your Own Model</li>
            <li>RAG Pipelines — Make AI Know Your Data</li>
            <li>Building AI Agents That Take Action</li>
            <li>Full-Stack AI Apps with Bolt.new</li>
            <li>AI-Powered Development with Cursor</li>
          </ul>
        </>
      ),
      skills: [
        'LLMs',
        'OpenAI API',
        'LangChain',
        'Cursor',
        'Bolt.new',
        'RAG',
        'Fine-Tuning',
        'AI Agents',
      ],
      anchor: 'master-ai',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Tech Stack Specialisation (Advanced)',
      image: null,
      duration: '4 to 8 weeks',
      tag: null,
      data: (
        <>
          <p>
            Push your specialization to senior-engineer level — the
            architectural depth top product companies expect from candidates
            targeting 15+ LPA roles.
          </p>

          <p>
            <span className="font-bold">Option 1 — Frontend Advanced:</span>{' '}
            Master the architectural concepts that separate good frontend devs
            from great ones — through hands-on workshops, activities & projects
          </p>
          <ul>
            <li>Browser caching strategies for blazing-fast load times</li>
            <li>Frontend security — XSS, CSRF, authentication patterns</li>
            <li>
              Website performance optimization with Lighthouse, profiling & CDN
            </li>
            <li>Redux state management for complex applications</li>
            <li>
              Server-Side Rendering with Next.js for SEO & first paint speed
            </li>
            <li>
              Advanced CSS architecture (BEM, CSS-in-JS, responsive systems)
            </li>
          </ul>

          <p>
            <span className="font-bold">Option 2 — Backend Advanced:</span> Make
            your QEats backend production-grade — the way real engineers scale
            Swiggy or Zomato
          </p>
          <ul>
            <li>Build advanced search features over large datasets</li>
            <li>
              Identify and fix performance bottlenecks using profiling tools
            </li>
            <li>
              Improve REST API throughput with caching (Redis), load testing
              (JMeter), and multi-threading
            </li>
            <li>Apply patterns the way senior backend engineers do</li>
          </ul>
        </>
      ),
      skills: [
        'Redux',
        'Next.js',
        'SSR',
        'CDN',
        'Advanced CSS',
        'SEO',
        'Caching',
        'Security',
        'Redis',
        'JMeter',
        'Multi-threading',
        'Performance Tuning',
      ],
      anchor: 'tech-stack-advanced',
      cardTitle: null,
      showOptionsAboveTitle: true,
      options: [
        { tag: 'frontend-advanced', title: 'Frontend Advanced' },
        { tag: 'backend-advanced', title: 'Backend Advanced' },
      ],
      cards: [],
    },
    {
      title: 'System Design Foundations',
      image: null,
      duration: '8 weeks',
      tag: null,
      data: (
        <>
          <p>
            Get interview-ready on both Low Level Design and High Level Design —
            the two pillars every product company uses to test senior
            engineering candidates.
          </p>

          <p>
            <span className="font-bold">
              LLD Foundations — Build & Design Like an Engineer:
            </span>
          </p>
          <ul>
            <li>
              <span className="font-bold">QContest</span> — Build a
              HackerRank-like contest platform applying Core Java & Low Level
              Design (SOLID principles, OOP, Object Modeling, Clean
              Architecture, Design Patterns)
            </li>
            <li>
              <span className="font-bold">JukeBox</span> — Build a Spotify-like
              jukebox that plays songs from a playlist, applying machine coding
              techniques and clean architecture
            </li>
          </ul>

          <p>
            <span className="font-bold">
              HLD Foundations — Architect Internet-Scale Systems:
            </span>
          </p>
          <ul>
            <li>
              Understand the architecture of modern web applications used
              universally by internet companies
            </li>
            <li>
              Hands-on with the production tech stack: Docker, Kafka,
              Microservices, CI/CD, Cloud
            </li>
            <li>
              Reason about CAP Theorem, SQL vs NoSQL trade-offs, partitioning,
              and replication
            </li>
            <li>
              Design for scalability with load balancing, caching layers, and
              horizontal scaling patterns
            </li>
          </ul>
        </>
      ),
      skills: [
        'SOLID Principles',
        'OOD',
        'Object Modeling',
        'Clean Architecture',
        'Design Patterns',
        'Machine Coding',
        'Microservices',
        'Docker',
        'Kafka',
        'CI/CD',
        'Cloud',
        'CAP Theorem',
        'SQL/NoSQL',
        'Scalability',
        'Load Balancing',
        'Partitioning',
        'Caching',
      ],
      anchor: 'system-design',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Interview Blitz with AI-Powered Mocks',
      image: null,
      duration: '4 weeks',
      tag: null,
      data: (
        <>
          <p>
            An intensive month of interview rehearsal — designed so you walk
            into every real interview already having faced the hardest
            questions.
          </p>
          <ul>
            <li>
              <span className="font-bold">DSA Blitz Sessions</span> — Crack DSA
              interviews through repeated, time-boxed problem-solving rounds
              that build speed and confidence
            </li>
            <li>
              <span className="font-bold">Multiple Timed Mock Assessments</span>{' '}
              — Simulate the pressure and pacing of real coding rounds at top
              product companies
            </li>
            <li>
              <span className="font-bold">AI-Powered Mock Interviews</span> —
              Practice with an AI interviewer that adapts to your level, gives
              instant feedback, and lets you rehearse 24/7
            </li>
            <li>
              <span className="font-bold">Peer-to-Peer (P2P) Interviews</span> —
              Take both sides of the table — interviewing peers sharpens your
              own answers
            </li>
            <li>
              <span className="font-bold">Resume Defense Prep</span> — Be ready
              to defend every single skill keyword and project on your resume
            </li>
            <li>
              <span className="font-bold">Specialization-Specific Prep</span> —
              Targeted prep for your FDT or BDT track, plus take-home assignment
              practice
            </li>
          </ul>
        </>
      ),
      skills: [
        'DSA Mocks',
        'DSA Blitz',
        'P2P Interviews',
        'Take-Home Prep',
        'Specialization Prep',
        'AI-Powered Mocks',
        'Resume Defense',
      ],
      anchor: 'interview',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Crio Externship with AI-Driven Use Cases',
      image: null,
      duration: '4 weeks',
      tag: null,
      data: (
        <>
          <p>
            Cap the program with a real externship — not a capstone. Solve
            actual problem statements sourced from top Indian tech companies,
            with AI woven into every challenge.
          </p>
          <ul>
            <li>
              <span className="font-bold">Real Industry Projects</span> — Go
              beyond typical bootcamp capstones — tackle live problem statements
              contributed by working teams at real companies
            </li>
            <li>
              <span className="font-bold">AI-Driven Use Cases</span> — Every
              externship integrates an emerging AI angle so you graduate with a
              portfolio that's relevant for 2026 hiring
            </li>
            <li>
              <span className="font-bold">Industry Mentorship</span> — Get
              reviewed and mentored by engineers from the partner companies
              themselves
            </li>
            <li>
              <span className="font-bold">Past Externship Partners</span> —
              CRED, Jumbotail, Groww, Vicara, Slice, Recko — and many more
              rotating partners every cohort
            </li>
            <li>
              <span className="font-bold">Resume-Ready Outcome</span> — Walk
              away with a verified, real-company project entry on your resume —
              a massive differentiator at the offer stage
            </li>
          </ul>
        </>
      ),
      skills: [
        'CRED',
        'Jumbotail',
        'Groww',
        'Vicara',
        'Slice',
        'Recko',
        'Industry Mentors',
        'AI Integration',
      ],
      anchor: 'externship',
      cardTitle: null,
      cards: [],
    },
  ],

  advancedCurriculum: [
    {
      title: 'Low Level Design',
      image: null,
      duration: '11 weeks',
      tag: null,
      data: (
        <>
          <p>
            Master designing & developing industry-grade applications using OOD
            principles & design patterns
          </p>

          <ul>
            <li>
              <span className="font-bold">Applied OOPs Foundations</span>: Apply
              OOP concepts in practical scenarios; practice IS-A, HAS-A, USES-A
              relationships
            </li>
            <li>
              Design clean and reusable industry-grade applications with
              reusable, modular, extensible software
            </li>
            <li>Apply SOLID design principles when designing your solution</li>
            <li>
              Learn important design patterns like Singleton, Factory, Builder,
              Strategy
            </li>
            <li>
              Practice converting PRDs into class diagrams and sequence diagrams
            </li>
            <li>
              Go deeper into schema design and how it impacts extensibility,
              speed & querying
            </li>
          </ul>
        </>
      ),
      skills: [
        'Encapsulation',
        'Abstraction',
        'Inheritance',
        'Polymorphism',
        'SOLID Principles',
        'Design Patterns',
        'Singleton',
        'Factory',
        'Builder',
        'Strategy',
        'Class Diagram',
        'Sequence Diagram',
        'Machine Coding',
        'Schema Design',
        'Normalization',
      ],
      anchor: 'dsa',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'High Level Design',
      image: null,
      duration: '11 weeks',
      tag: null,
      data: (
        <>
          <ul>
            <li>
              Understand design choices across compute, data-store &
              communication for large-scale distributed systems like Flipkart
            </li>
            <li>
              Work hands-on with ZCommerce — Crio's distributed
              microservices-based scalable application on the cloud
            </li>
            <li>
              Contrast microservice with event-driven, big-compute, big-data
              architectures
            </li>
            <li>
              Learn why ZCommerce uses both EC2 (IaaS) & Elastic Beanstalk
              (PaaS)
            </li>
            <li>
              Classify workloads as read-heavy, write-heavy, transactional to
              choose the right data store
            </li>
            <li>
              Understand REST API vs gRPC trade-offs and decrease response time
              using CDN
            </li>
          </ul>
        </>
      ),
      skills: [
        'Microservices',
        'Event-driven Architecture',
        'Containers',
        'Serverless',
        'Virtual Machines',
        'SQL vs NoSQL',
        'Blob',
        'Time Series DB',
        'Columnar DB',
        'CAP Theorem',
        'Transactions',
        'Data Consistency',
        'REST API',
        'gRPC',
        'CDN',
        'S3',
      ],
      anchor: 'dsa',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Advanced Interview Blitz',
      image: null,
      duration: '2 weeks',
      tag: null,
      data: (
        <>
          <ul>
            <li>Prepare to take on LLD interviews with confidence</li>
            <li>
              Master cracking machine coding rounds with quick designs and
              structured execution
            </li>
            <li>Optimize code by using better data structures</li>
            <li>
              Practice LLD design questions like BookMyShow, Splitwise, and
              Trello — commonly asked at leading product companies
            </li>
          </ul>
        </>
      ),
      skills: [
        'LLD',
        'Machine Coding',
        'Schema Design',
        'Object Modeling',
        'Virtual Machines',
        'Interview Preparation',
      ],
      anchor: 'dsa',
      cardTitle: null,
      cards: [],
    },
    {
      title: 'Build & Ship Production AI Systems',
      image: null,
      duration: '8 Sessions · 16 Hours',
      tag: null,
      new: true,
      data: (
        <>
          <ul>
            <li>
              Building LLM-Powered Applications (streaming, async, context
              windows)
            </li>
            <li>
              Vibe Coding with Claude Code & Cursor (AI-native development
              workflows)
            </li>
            <li>
              From Single Agents to Multi-Agent Systems (orchestration,
              communication)
            </li>
            <li>AI Agents That Integrate Everything (MCP, tool ecosystems)</li>
            <li>
              RAG Systems: Search Anything with AI (vector stores, re-ranking,
              eval)
            </li>
            <li>
              How to Know If Your AI Actually Works (metrics, evaluation,
              debugging)
            </li>
            <li>
              Production AI: Scaling, Cost & Monitoring (latency, error
              handling, observability)
            </li>
            <li>
              Capstone: Build & Ship a Real AI Product (from idea to deployment)
            </li>
          </ul>
        </>
      ),
      skills: [
        'LangGraph',
        'Claude Code',
        'Cursor',
        'MCP',
        'FAISS',
        'Pinecone',
        'RAGAS',
        'Langfuse',
      ],
      anchor: 'build-ship-ai',
      cardTitle: null,
      cards: [],
    },
  ],
};
